export const DRAWER_DIRECTIONS = Object.freeze({
    LEFT: 'left',
    RIGHT: 'right',
    BOTTOM: 'bottom',
});

export const DRAWER_VARIANTS = Object.freeze({
    IN_PLACE: 'in-place',
    OVER: 'over',
});

export const DRAWER_SCOPES = Object.freeze({
    CONTAINER: 'container',
    PARENT: 'parent',
    VIEWPORT: 'viewport',
});

const HORIZONTAL_DIRECTIONS = new Set([
    DRAWER_DIRECTIONS.LEFT,
    DRAWER_DIRECTIONS.RIGHT,
]);

const DRAWER_SCOPE_VALUES = new Set(Object.values(DRAWER_SCOPES));

/**
 * Normalizes the public direction value to a supported drawer direction.
 * @param {string|null|undefined} value Configured direction.
 * @returns {'left'|'right'|'bottom'} A supported direction.
 */
export function normalizeDrawerDirection(value) {
    return HORIZONTAL_DIRECTIONS.has(value) || value === DRAWER_DIRECTIONS.BOTTOM
        ? value
        : DRAWER_DIRECTIONS.RIGHT;
}

/**
 * Resolves the direction currently used to render the component. The legacy
 * mobile-presentation API is intentionally kept at this boundary so the rest
 * of the component can work with one direction model.
 * @param {object} options Presentation options.
 * @param {string|null|undefined} options.direction Configured drawer direction.
 * @param {string|null|undefined} options.mobilePresentation Legacy mobile presentation.
 * @param {string|number|null|undefined} options.mobileBreakPoint Legacy breakpoint.
 * @param {number} options.viewportWidth Current viewport width.
 * @returns {'left'|'right'|'bottom'} Effective rendering direction.
 */
export function resolveDrawerDirection({
    direction,
    mobilePresentation,
    mobileBreakPoint,
    viewportWidth,
}) {
    const normalizedDirection = normalizeDrawerDirection(direction);
    if (normalizedDirection === DRAWER_DIRECTIONS.BOTTOM) {
        return DRAWER_DIRECTIONS.BOTTOM;
    }

    const parsedBreakPoint = Number.parseFloat(mobileBreakPoint);
    const usesLegacyBottomPresentation = mobilePresentation === 'bottom-sheet'
        && Number.isFinite(parsedBreakPoint)
        && viewportWidth <= parsedBreakPoint;

    return usesLegacyBottomPresentation
        ? DRAWER_DIRECTIONS.BOTTOM
        : normalizedDirection;
}

/**
 * Returns whether a direction uses horizontal drawer geometry.
 * @param {string} direction Value selected by the presentation resolver.
 * @returns {boolean} True for left and right.
 */
export function isHorizontalDrawerDirection(direction) {
    return HORIZONTAL_DIRECTIONS.has(direction);
}

/**
 * Resolves the responsive side variant without mutating the public attribute.
 * Bottom drawers always use overlay geometry.
 * @param {object} options Inputs needed to choose flow or overlay geometry.
 * @param {string} options.direction Effective drawer direction.
 * @param {string} options.variant Configured variant.
 * @param {string|number|null|undefined} options.screenBreakPoint Side breakpoint.
 * @param {number} options.viewportWidth Current viewport width.
 * @returns {'in-place'|'over'} Effective variant.
 */
export function resolveDrawerVariant({
    direction,
    variant,
    screenBreakPoint,
    viewportWidth,
}) {
    if (direction === DRAWER_DIRECTIONS.BOTTOM) return DRAWER_VARIANTS.OVER;

    const normalizedVariant = variant === DRAWER_VARIANTS.OVER
        ? DRAWER_VARIANTS.OVER
        : DRAWER_VARIANTS.IN_PLACE;
    const parsedBreakPoint = Number.parseFloat(screenBreakPoint);

    if (Number.isFinite(parsedBreakPoint) && viewportWidth <= parsedBreakPoint) {
        return DRAWER_VARIANTS.OVER;
    }

    return normalizedVariant;
}

/**
 * Resolves the frame used by a bottom drawer. A directly configured bottom
 * drawer belongs to its surrounding container by default. The old responsive
 * bottom-sheet adapter keeps its historical viewport default.
 * @param {object} options Scope inputs.
 * @param {string|null|undefined} options.scope Preferred public scope.
 * @param {string|null|undefined} options.legacyScope Legacy sheet scope.
 * @param {string|null|undefined} options.direction Configured direction.
 * @returns {'container'|'parent'|'viewport'} Effective bottom drawer scope.
 */
export function resolveDrawerScope({ scope, legacyScope, direction }) {
    if (DRAWER_SCOPE_VALUES.has(scope)) return scope;
    if (DRAWER_SCOPE_VALUES.has(legacyScope)) return legacyScope;

    return normalizeDrawerDirection(direction) === DRAWER_DIRECTIONS.BOTTOM
        ? DRAWER_SCOPES.CONTAINER
        : DRAWER_SCOPES.VIEWPORT;
}

/**
 * Resolves a configured bottom drawer height against the current viewport.
 * @param {string|number|null|undefined} value CSS-like height value.
 * @param {number} fallback Fallback pixel value.
 * @param {object} viewport Viewport dimensions.
 * @param {number} viewport.width Horizontal reference used by `vw` values.
 * @param {number} viewport.height Vertical reference used by `vh` and `%` values.
 * @returns {number} Resolved pixel height.
 */
export function resolveDrawerHeight(value, fallback, viewport) {
    if (!value || value === 'auto') return fallback;

    const normalizedValue = `${value}`.trim();
    const parsedValue = Number.parseFloat(normalizedValue);
    if (Number.isNaN(parsedValue)) return fallback;
    if (normalizedValue.endsWith('px')) return parsedValue;
    if (normalizedValue.endsWith('vh')) return viewport.height * parsedValue / 100;
    if (normalizedValue.endsWith('vw')) return viewport.width * parsedValue / 100;
    if (normalizedValue.endsWith('%')) return viewport.height * parsedValue / 100;
    if (/^-?\d+(\.\d+)?$/.test(normalizedValue)) return parsedValue;

    return fallback;
}

/**
 * Clamps a drawer size between its configured limits.
 * @param {number} value Requested size.
 * @param {number} min Minimum size.
 * @param {number} max Maximum size.
 * @returns {number} Clamped size.
 */
export function clampDrawerSize(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
